// ============================================
// Contexto de autenticação (sessão por cookie).
// useAuth() → { user, isAdmin, isPlatformAdmin, loading, login, logout, switchCompany }
//
// F4 — multi-empresa: o user carrega a empresa ATIVA (companyId), o papel DO
// VÍNCULO (companyRole) e a lista de vínculos (memberships) para o switcher.
// isAdmin passa a derivar do papel do vínculo — admin na empresa A, operador na
// B. Trocar de empresa (switchCompany) zera TODO o cache do react-query: nenhuma
// queryKey carrega empresa, então dado da empresa anterior não pode sobreviver.
// ============================================

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, apiGet, apiPost, setActiveCompanyId, UNAUTHORIZED_EVENT, COMPANY_MISMATCH_EVENT } from '../services/api'

// platform_auditor = read-only global (SEC-002), análogo do platform_admin sem escrita.
export type Role = 'admin' | 'operator' | 'platform_admin' | 'platform_auditor'
// auditor = read-only por empresa (SEC-002), análogo do operator sem escrita.
export type CompanyRole = 'company_admin' | 'operator' | 'auditor'
/**
 * Vocabulário de papel do PAINEL de gestão de usuários (papel do VÍNCULO):
 * admin ⇔ company_admin · operator · auditor (read-only). platform_admin/
 * platform_auditor são globais (promoção manual) e não aparecem aqui.
 */
export type PanelRole = 'admin' | 'operator' | 'auditor'

export interface Membership {
  companyId: string
  companyName: string
  role: CompanyRole
}

export interface AuthUser {
  id: string
  email: string
  name: string | null
  /** Papel GLOBAL da conta. 'admin' legado e 'platform_admin' valem em toda a plataforma. */
  role: Role
  /** Empresa ATIVA da sessão (null = conta sem vínculo — estado degradado). */
  companyId: string | null
  /** Papel do vínculo ATIVO — decide "admin da empresa". */
  companyRole: CompanyRole | null
  memberships: Membership[]
}

interface AuthContextValue {
  user: AuthUser | null
  /** Admin NA EMPRESA ATIVA (papel do vínculo) ou admin global (legado/plataforma). */
  isAdmin: boolean
  /** Dono da plataforma (admin legado conta): ações que afetam TODAS as empresas. */
  isPlatformAdmin: boolean
  /** Leitura de plataforma: platform_admin + platform_auditor (vê /empresas, read-only). */
  isPlatformViewer: boolean
  /** Papel READ-ONLY (auditor de empresa OU de plataforma) — esconde controles de escrita. */
  isAuditor: boolean
  /** Pode gravar dados operacionais? Qualquer não-auditor logado. (Escrita admin-only usa isAdmin.) */
  canWrite: boolean
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  /** F4 — troca a empresa ativa da sessão e zera os caches. Resolve com o novo user. */
  switchCompany: (companyId: string) => Promise<AuthUser>
  /** Re-busca /me (ex.: memberships mudaram — empresa criada). NÃO mexe em caches. */
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const qc = useQueryClient()

  // O header X-Company-Id acompanha a empresa ativa desta aba (guarda anti-aba-
  // cruzada). Este efeito é a REDE DE SEGURANÇA; nos caminhos de troca o header
  // é publicado SINCRONAMENTE antes do clear do cache — os effects dos FILHOS
  // (que refazem fetch pós-clear) rodam antes deste effect do provider, e sem a
  // publicação síncrona toda troca dispararia uma rajada de 409 contra si mesma.
  useEffect(() => {
    setActiveCompanyId(user?.companyId ?? null)
  }, [user?.companyId])

  useEffect(() => {
    let cancelled = false
    apiGet<{ user: AuthUser }>('/api/auth/me')
      .then((data) => {
        if (!cancelled) setUser(data.user)
      })
      .catch(() => {
        if (!cancelled) setUser(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    // Cache morre junto com a sessão: o próximo login (que pode ser de OUTRA
    // empresa, na mesma aba) não pode herdar listas da sessão anterior.
    const onUnauthorized = () => { qc.clear(); setUser(null) }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [qc])

  // F4 — outra aba trocou a empresa ativa (409 company_mismatch): adota o novo
  // contexto (re-busca /me) e zera os caches — nunca mistura dados de empresas.
  useEffect(() => {
    const onMismatch = () => {
      apiGet<{ user: AuthUser }>('/api/auth/me')
        .then((data) => {
          setActiveCompanyId(data.user.companyId ?? null) // síncrono, ANTES do reset
          void qc.cancelQueries().then(() => qc.resetQueries())
          setUser(data.user)
        })
        .catch((err: unknown) => {
          // Só 401 é perda de sessão; falha transitória (rede/5xx) mantém o
          // user — o próximo request defasado re-dispara o mismatch (retry natural).
          if (err instanceof ApiError && err.status === 401) setUser(null)
        })
    }
    window.addEventListener(COMPANY_MISMATCH_EVENT, onMismatch)
    return () => window.removeEventListener(COMPANY_MISMATCH_EVENT, onMismatch)
  }, [qc])

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiPost<{ user: AuthUser }>('/api/auth/login', { email, password })
    // Defesa em profundidade: nada da sessão/empresa anterior sobrevive ao login.
    qc.clear()
    setUser(data.user)
  }, [qc])

  const logout = useCallback(async () => {
    try {
      await apiPost('/api/auth/logout')
    } catch {
      // sessão já pode ter expirado — seguir com logout local
    }
    qc.clear()
    setUser(null)
  }, [qc])

  const switchCompany = useCallback(async (companyId: string): Promise<AuthUser> => {
    const data = await apiPost<{ user: AuthUser }>('/api/auth/switch-company', { companyId })
    // Ordem importa: header da empresa nova SINCRONAMENTE primeiro (os refetches
    // saem dos effects dos FILHOS, antes do effect deste provider — header velho
    // seria uma rajada de 409 contra si mesmo); cancela os requests em voo da
    // empresa anterior e RESETA as queries: reset (≠ clear) zera os dados E
    // notifica os observers montados, rebuscando as ativas — clear() deixaria
    // providers que não remontam (ex.: VerticalProvider no layout → breadcrumb)
    // exibindo a empresa anterior para sempre. Dado velho não fica visível:
    // reset volta ao estado inicial (loading), não a stale-while-refetch.
    setActiveCompanyId(data.user.companyId ?? null)
    await qc.cancelQueries()
    void qc.resetQueries()
    setUser(data.user)
    return data.user
  }, [qc])

  // F4 — memberships mudaram fora do fluxo de login/troca (ex.: empresa recém-
  // criada com auto-vínculo): re-adota o /me sem tocar nos caches — a empresa
  // ATIVA não mudou, só o menu do switcher precisa refletir o vínculo novo.
  const refreshUser = useCallback(async () => {
    const data = await apiGet<{ user: AuthUser }>('/api/auth/me')
    setUser(data.user)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => {
      // SEC-002: auditor de empresa (companyRole) ou de plataforma (role) — só leitura.
      const isAuditor = user?.companyRole === 'auditor' || user?.role === 'platform_auditor'
      return {
        user,
        isAdmin:
          user?.role === 'admin' || user?.role === 'platform_admin' || user?.companyRole === 'company_admin',
        isPlatformAdmin: user?.role === 'admin' || user?.role === 'platform_admin',
        isPlatformViewer:
          user?.role === 'admin' || user?.role === 'platform_admin' || user?.role === 'platform_auditor',
        isAuditor,
        canWrite: !!user && !isAuditor,
        loading,
        login,
        logout,
        switchCompany,
        refreshUser,
      }
    },
    [user, loading, login, logout, switchCompany, refreshUser]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>')
  return ctx
}
